import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { useActions, useState } from 'vuex-composition-helpers'
import { mdiAccountOutline, mdiCogOutline, mdiDnsOutline } from '@mdi/js'
import { subject } from '@casl/ability'
import { PRIVATE } from '@/plugins/acl/const'
import {
  areQueriesEqual,
  cleanupQuery,
  parseFilterArrayFromQuery,
  parseTableOptionsFromQuery,
  readListQueryFromSession,
  serializeFilterArrayToQuery,
  serializeTableOptionsToQuery,
  writeListQueryToSession,
} from '@/utils/listRouteState'

/** Query keys for this tab — prefixed so they never clash with jobs-list params on the same route. */
const CANDIDATE_QUERY_KEYS = ['cStage', 'cQ', 'cPage', 'cSize', 'cSortBy', 'cSortDesc']

const candidateListParamsPresent = (query) => {
  const cleaned = cleanupQuery(query)
  return CANDIDATE_QUERY_KEYS.some((k) => Object.prototype.hasOwnProperty.call(cleaned, k))
}

const stripCandidateQueryKeys = (query) => {
  const next = { ...query }
  CANDIDATE_QUERY_KEYS.forEach((k) => {
    delete next[k]
  })
  return next
}

const parseCandidateSearchFromQuery = (query) => (typeof query.cQ === 'string' ? query.cQ : '')

const serializeCandidateSearchToQuery = (searchQuery) => (searchQuery ? { cQ: searchQuery } : {})

const parseCandidateTableOptionsFromQuery = (query, defaults) =>
  parseTableOptionsFromQuery(
    {
      page: query.cPage,
      size: query.cSize,
      sortBy: query.cSortBy,
      sortDesc: query.cSortDesc,
    },
    defaults,
  )

const serializeCandidateTableOptionsToQuery = (options) => {
  const t = serializeTableOptionsToQuery(options)
  return cleanupQuery({
    cPage: t.page,
    cSize: t.size,
    cSortBy: t.sortBy,
    cSortDesc: t.sortDesc,
  })
}

const listSessionKeyForJob = (jobId) => `apps-ats-job-view-candidates:${jobId}`

export default function useCandidateList() {
  const vm = getCurrentInstance().proxy

  const candidates = ref([])
  const choices = ref({})
  const selected = ref([])

  const stageFilter = ref([])

  const tableColumns = [
    { text: 'name', value: 'name', class: 'text-uppercase' },
    { text: 'status', value: 'stage', class: 'text-uppercase' },
    { text: 'step', value: 'step', class: 'text-uppercase' },
    { text: 'gdprStatus', value: 'gdprStatus', class: 'text-uppercase' },
    {
      text: 'actions',
      value: 'actions',
      align: 'center',
      sortable: false,
      class: 'text-uppercase',
    },
  ]

  const searchQuery = ref('')
  const totalCandidatesListTable = ref(0)
  const loading = ref(false)
  const options = ref({
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  })
  const userTotalLocal = ref([])
  const overview = ref([])
  const isSyncingFromRoute = ref(false)

  const { getJobCandidatesFilters, getJobCandidates, getJobCandidatesOverview } = useActions('jobs', [
    'getJobCandidatesFilters',
    'getJobCandidates',
    'getJobCandidatesOverview',
  ])

  const { jobData } = useState('jobs', ['jobData'])

  const fetchCandidatesOverview = async () => {
    overview.value = await getJobCandidatesOverview(jobData.value.id)
  }

  const applyStateFromRoute = () => {
    const jobId = vm.$route.params.id
    if (!jobId) return

    isSyncingFromRoute.value = true

    let query = { ...vm.$route.query }
    if (!candidateListParamsPresent(query)) {
      const stored = readListQueryFromSession(listSessionKeyForJob(jobId))
      if (stored) {
        query = { ...query, ...stored }
      }
    }

    stageFilter.value = parseFilterArrayFromQuery(query, 'cStage')
    searchQuery.value = parseCandidateSearchFromQuery(query)
    options.value = parseCandidateTableOptionsFromQuery(query, options.value)

    isSyncingFromRoute.value = false
  }

  const syncStateToRoute = async () => {
    if (isSyncingFromRoute.value) return

    const jobId = vm.$route.params.id
    if (!jobId) return

    const candidateSlice = cleanupQuery({
      ...serializeFilterArrayToQuery('cStage', stageFilter.value),
      ...serializeCandidateSearchToQuery(searchQuery.value),
      ...serializeCandidateTableOptionsToQuery(options.value),
    })

    const nextQuery = cleanupQuery({
      ...stripCandidateQueryKeys(vm.$route.query),
      ...candidateSlice,
    })

    if (!areQueriesEqual(vm.$route.query, nextQuery)) {
      await vm.$router.replace({ query: nextQuery })
    }
    writeListQueryToSession(listSessionKeyForJob(jobId), candidateSlice)
  }

  const fetchCandidates = async () => {
    loading.value = true
    if (!jobData.value.id) {
      loading.value = false
      return
    }
    const { data, success } = await getJobCandidates({
      jobId: jobData.value.id,
      stage: stageFilter.value,
      search: searchQuery.value,
      page: options.value.page,
      pageSize: options.value.itemsPerPage,
      ordering: options.value.sortBy.map((el, index) => {
        const parsedEl = el
          .replaceAll('.', '__')
          .replace('gdprStatus', 'gdpr_status')
          .replace('name', 'candidate__user__last_name')

        return `${options.value.sortDesc[index] ? '-' : ''}${parsedEl}`
      }),
    })
    if (success) {
      if (Object.hasOwnProperty.bind(data)('results') && Object.hasOwnProperty.bind(data)('count')) {
        candidates.value = data.results
        totalCandidatesListTable.value = data.count
      } else {
        candidates.value = data
        totalCandidatesListTable.value = data.length
      }
      candidates.value.forEach((candidate) => {
        // eslint-disable-next-line no-param-reassign
        if (['rejected', 'hired'].includes(candidate.stage)) candidate.disabled = true
        subject(PRIVATE.ATS.JOB_CANDIDATE, candidate)
      })
      loading.value = false
    }

    choices.value = await getJobCandidatesFilters({
      jobId: jobData.value.id,
    })
  }

  const timer = ref(null)
  const delaySearchHandler = async () => {
    clearTimeout(timer.value)
    timer.value = setTimeout(async () => {
      await syncStateToRoute()
      await fetchCandidates()
    }, 1000)
  }

  applyStateFromRoute()

  watch(
    () => vm.$route.params.id,
    () => {
      applyStateFromRoute()
    },
  )

  watch(
    [stageFilter, options],
    async () => {
      await syncStateToRoute()
      await fetchCandidates()
    },
    { immediate: true },
  )

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveCandidateStageColor = (stage) => {
    if (stage === 'new') return { text: 'new', color: 'info' }
    if (stage === 'in_progress') return { text: 'inProgress', color: 'primary_c5' }
    if (stage === 'hired') return { text: 'hired', color: 'success' }
    if (stage === 'rejected') return { text: 'rejected', color: 'error' }

    return { text: '-/-', color: 'primary' }
  }

  const resolveGDPRStatusColor = (status) => {
    if (status === 'valid') return { text: 'valid', color: 'success' }
    if (status === 'expired') return { text: 'expired', color: 'warning' }
    if (status === 'anonymized') return { text: 'anonymized', color: 'accent' }
    if (status === 'autoAnonymizationDisabled') return { text: 'Auto anonymization disabled', color: 'secondary' }

    return { text: '-/-', color: 'primary' }
  }

  const resolveCandidatesTotalIcon = (title) => {
    if (title === 'total') return { icon: mdiCogOutline, color: 'secondary' }
    if (title === 'new') return { icon: mdiDnsOutline, color: 'primary' }
    if (title === 'in_progress') return { icon: mdiAccountOutline, color: 'success' }

    return { icon: mdiAccountOutline, color: 'primary' }
  }

  return {
    fetchCandidates,
    fetchCandidatesOverview,
    resolveCandidateStageColor,
    resolveGDPRStatusColor,
    resolveCandidatesTotalIcon,
    delaySearchHandler,

    tableColumns,
    searchQuery,
    totalCandidatesListTable,
    loading,
    options,
    userTotalLocal,
    overview,
    candidates,
    choices,
    selected,
    stageFilter,
  }
}
